'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

type Option<T extends string> = { label: string; value: T };

type Props<T extends string> = {
  label: string;
  options: Option<T>[];
  selected: T[];
  multiple: boolean;
  onToggle: (value: T) => void;
};

export function FilterCombobox<T extends string>({ label, options, selected, multiple, onToggle }: Props<T>) {
  const t = useTranslations('filters');
  const [open, setOpen] = useState(false);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);

  const selectedOptions = options.filter((option) => selected.includes(option.value));
  const hasValue = selectedOptions.length > 0;
  const summary = !hasValue
    ? t('parameter_not_selected')
    : multiple
      ? `${t('selected')}: ${selectedOptions.length}`
      : selectedOptions[0].label;

  const handleSelect = (value: T) => {
    onToggle(value);
    if (!multiple) {
      setOpen(false);
    }
  };

  return (
    <div className='flex flex-col gap-[8px]'>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          setIsKeyboardNav(false);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            aria-label={label}
            className={clsx(
              'h-[44px] w-full justify-between rounded-[12px] border-primary-stroke-day bg-white px-[12px] text-[16px] font-normal hover:bg-white active:scale-100 data-[state=open]:border-primary-green-day',
              hasValue ? 'border-primary-green-day text-text-primary-day' : 'text-text-disabled-day',
            )}
          >
            <span className='truncate'>{summary}</span>
            <IconChevronDown
              className={clsx(
                'size-[18px] shrink-0 text-icon-default transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-(--radix-popover-trigger-width) min-w-[240px] p-0'>
          <Command
            disablePointerSelection
            data-keyboard-nav={isKeyboardNav}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                setIsKeyboardNav(true);
              }
            }}
            onPointerMove={() => setIsKeyboardNav(false)}
            className='group/command'
          >
            <CommandInput
              placeholder={t('search_in_list')}
              className='h-full rounded-none py-0 text-[15px] focus:placeholder:text-transparent'
            />
            <CommandList className='p-[4px]'>
              <CommandEmpty className='py-[20px] text-center text-[14px] text-text-disabled-day'>
                {t('nothing_found')}
              </CommandEmpty>
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                    className='min-h-[40px] cursor-pointer gap-[10px] px-[10px] text-[15px] text-text-primary-day hover:bg-brand-accent data-[selected=true]:bg-transparent data-[selected=true]:hover:bg-brand-accent group-data-[keyboard-nav=true]/command:data-[selected=true]:bg-brand-accent'
                  >
                    {multiple && <Checkbox checked={isSelected} tabIndex={-1} className='pointer-events-none' />}
                    <span className='flex-1 truncate'>{option.label}</span>
                    {!multiple && isSelected && <IconCheck className='size-[18px] text-primary-green-day' />}
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {multiple && selectedOptions.length > 0 && (
        <div className='flex flex-wrap gap-[6px]'>
          {selectedOptions.map((option) => (
            <Badge
              key={option.value}
              variant='secondary'
              className='h-[28px] gap-[4px] rounded-full bg-light-green ps-[10px] pe-[4px] text-[13px] font-normal text-text-primary-day'
            >
              {option.label}
              <Button
                variant='ghost'
                size='icon'
                onClick={() => onToggle(option.value)}
                aria-label={t('remove_filter', { name: option.label })}
                className='size-[20px] rounded-full text-text-disabled-day hover:bg-white hover:text-text-primary-day active:scale-90'
              >
                <IconX className='size-[14px]' />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
